import type { InternalNode, Node } from '@xyflow/react';
import type { FlowchartNodeData } from './flowchart-types';
import type { NodeRect } from './flowchart-edge-geometry';

export interface LayoutCenter {
  x: number;
  y: number;
}

function getMeasuredNodeSize(node: InternalNode): {
  height?: number;
  width?: number;
} {
  return {
    height: node.measured.height ?? node.height ?? undefined,
    width: node.measured.width ?? node.width ?? undefined,
  };
}

function isMeasuredSize(input: {
  height?: number;
  width?: number;
}): input is { height: number; width: number } {
  return typeof input.width === 'number' && typeof input.height === 'number';
}

export function toNodeRect(
  node: InternalNode<Node<FlowchartNodeData>>,
): NodeRect | null {
  const size = getMeasuredNodeSize(node);
  if (!isMeasuredSize(size)) {
    return null;
  }

  const topLeft = node.internals.positionAbsolute;
  return {
    center: {
      x: topLeft.x + size.width / 2,
      y: topLeft.y + size.height / 2,
    },
    size,
    shape: node.data.shape,
  };
}

export function selectLayoutCenter(
  nodeLookup: ReadonlyMap<string, InternalNode>,
): LayoutCenter {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  nodeLookup.forEach((node) => {
    const size = getMeasuredNodeSize(node);
    if (!isMeasuredSize(size)) {
      return;
    }

    const topLeft = node.internals.positionAbsolute;
    const centerX = topLeft.x + size.width / 2;
    const centerY = topLeft.y + size.height / 2;
    minX = Math.min(minX, centerX);
    maxX = Math.max(maxX, centerX);
    minY = Math.min(minY, centerY);
    maxY = Math.max(maxY, centerY);
  });

  return {
    x: Number.isFinite(minX) ? (minX + maxX) / 2 : 0,
    y: Number.isFinite(minY) ? (minY + maxY) / 2 : 0,
  };
}
